import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Event } from '../../types/event.types';
import { FavoriteButton, ShareButton, NotificationBubble, DateIcon, ContentBlock, Button, Tag } from '../../pages/DesignSystem';
import './PreviewCardMap.css';
import { formatEventDateTime, formatEventDateForIcon, formatEventTime } from '../../utils/timezoneUtils';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useVisibilityTracker } from '../../hooks/useVisibilityTracker';
import { useSmartSeries } from '../../hooks/useSmartSeries';
import { groupDaysBySchedule } from '../../utils/seriesPatternUtils';
import MapDrawer from '../../pages/DesignSystem/patterns/MapDrawer';
import { useStableHandlers } from '../../hooks/useStableHandlers';
import { EnrichedEvent } from '../../stores/modalStackStore';
import { SecurityUtils } from '../../utils/security';
import { optimizeCloudinaryUrl } from '../../services/cloudinary.service';
import { getTagFallbackGradient, sortTags } from '../../pages/DesignSystem/types/tags.types';


interface PreviewCardMapProps {
  events: Event[];
  location: string;
  isOpen: boolean;
  onClose: () => void;
  onEventClick: (event: Event) => void;
  seriesEvents?: Event[];
  isSingleEvent?: boolean;
  enrichedEvents?: EnrichedEvent[]; // 🔄 Type unifié depuis modalStackStore
}

const FALLBACK_IMAGE = '/images/logoverttexte.webp';

// Composant EventDetails pour les événements récurrents
const EventDetails = React.memo(({
  event,
  onEndDateChange,
  seriesEvents
}: {
  event: Event;
  onEndDateChange?: (date?: Date) => void;
  seriesEvents?: Event[];
}) => {
  const { pattern, endDate, loading, series } = useSmartSeries(event.seriesId, undefined, event.organizerId, seriesEvents);
  const daySchedules = series?.daySchedules;
  const hasDaySchedules = daySchedules && Object.keys(daySchedules).length > 0;

  // Use seriesEvents directly from props (passed from EventCard → sortedSeriesEvents)
  const effectiveSeriesEvents = seriesEvents && seriesEvents.length > 0 ? seriesEvents : [];

  useEffect(() => {
    if (onEndDateChange) {
      onEndDateChange(endDate);
    }
  }, [endDate, onEndDateChange]);

  // Detect variable times: from daySchedules or from occurrences
  const variableTimeInfo = useMemo(() => {
    // Trim seconds from time strings (e.g., "09:00:00" → "09:00")
    const trimTime = (t: string) => t.replace(/^(\d{1,2}:\d{2}):\d{2}$/, '$1');

    const formatGroups = (groups: Array<{ days: string[]; start: string; end: string }>): string[] | string => {
      if (groups.length <= 3) {
        return groups.map(g => {
          const days = g.days.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
          return `${days} ${trimTime(g.start)}${g.end ? ` - ${trimTime(g.end)}` : ''}`;
        });
      }
      return 'Various times';
    };

    // Path 1: daySchedules from series
    if (hasDaySchedules && daySchedules) {
      const groups = groupDaysBySchedule(daySchedules);
      if (groups.length > 1) {
        return formatGroups(groups);
      }
      return null; // uniform — let original pattern handle it
    }

    // Path 2: deduce from occurrences (prop or cache fallback)
    if (effectiveSeriesEvents && effectiveSeriesEvents.length >= 2) {
      const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const timesByDay = new Map<string, { start: string; end: string }>();
      for (const evt of effectiveSeriesEvents) {
        if (!evt.datetime) continue;
        const d = new Date(evt.datetime);
        const dayName = DAY_NAMES[d.getDay()];
        if (timesByDay.has(dayName)) continue;
        const start = formatEventTime(evt.datetime, evt.timezone);
        const end = evt.endTime ? formatEventTime(evt.endTime, evt.timezone) : '';
        timesByDay.set(dayName, { start, end });
      }
      const uniqueTimes = new Set(Array.from(timesByDay.values()).map(v => `${v.start}|${v.end}`));
      if (uniqueTimes.size > 1) {
        const scheduleRecord: Record<string, { start: string; end: string }> = {};
        timesByDay.forEach((v, k) => { scheduleRecord[k] = v; });
        return formatGroups(groupDaysBySchedule(scheduleRecord));
      }
    }

    return null; // uniform times
  }, [hasDaySchedules, daySchedules, effectiveSeriesEvents]);

  // Only strip times from pattern when variable (otherwise keep full original pattern)
  const displayPattern = useMemo(() => {
    if (!pattern) return 'Recurring event';
    if (!variableTimeInfo) return pattern; // uniform → keep as-is with times

    // Variable → strip times from pattern, we show them separately below
    let simplified = pattern;
    simplified = simplified.replace(/\s*\([^)]*\d{1,2}:\d{2}[^)]*\)/g, '');
    simplified = simplified.replace(/\s*[-–]\s*\d{1,2}:\d{2}/gi, '');
    simplified = simplified.replace(/\s+at\s+\d{1,2}:\d{2}(\s*(AM|PM))?/gi, '');
    simplified = simplified.replace(/\s+/g, ' ').trim();
    return simplified || 'Recurring event';
  }, [pattern, variableTimeInfo]);

  return (
    <div className="preview-card-map__details">
      {event.seriesId ? (
        (loading && !pattern) ? (
          <div
            className="skeleton skeleton--text"
            style={{
              height: '1rem',
              width: '85%'
            }}
          />
        ) : (
          <>
            <span dangerouslySetInnerHTML={{ __html: SecurityUtils.sanitizeHtml(displayPattern) }} />
            {variableTimeInfo && (
              Array.isArray(variableTimeInfo) ? (
                <div className="preview-card-map__time preview-card-map__time--variable">
                  {variableTimeInfo.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              ) : (
                <span className="preview-card-map__time preview-card-map__time--variable">{variableTimeInfo}</span>
              )
            )}
          </>
        )
      ) : (
        <div className="preview-card-map__date-row">
          <DateIcon
            {...formatEventDateForIcon(event.datetime, event.timezone)}
            variant="light"
            size="sm"
          />
          <div className="preview-card-map__date-info">
            <span className="preview-card-map__day">
              <strong>{formatEventDateTime(event.datetime, event.timezone).weekday}</strong>
            </span>
            <span className="preview-card-map__time">
              {formatEventDateTime(event.datetime, event.timezone).time}
              {event.endTime && ` - ${formatEventTime(event.endTime, event.timezone)}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

// Composant EventCard pour chaque événement individuel
const EventCard = React.memo(({
  event,
  onSelect,
  onShareSuccess,
  sortedSeriesEvents,
  enrichedEvents,
  analytics,
  scrollContainerRef
}: {
  event: Event;
  onSelect: (event: Event) => void;
  onShareSuccess: () => void;
  sortedSeriesEvents?: Event[];
  enrichedEvents?: EnrichedEvent[];
  analytics: ReturnType<typeof useAnalytics>;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}) => {
  // Derive series events: from shared prop OR from enrichedEvents for this specific event
  const eventSeriesEvents = useMemo(() => {
    if (sortedSeriesEvents && sortedSeriesEvents.length > 0) return sortedSeriesEvents;
    if (enrichedEvents) {
      const enriched = enrichedEvents.find(
        e => e.main.id === event.id || (event.seriesId && e.main.seriesId === event.seriesId)
      );
      if (enriched?.series && enriched.series.length > 0) {
        const now = new Date();
        return (enriched.series as Event[])
          .filter(e => e.datetime && new Date(e.datetime) > now)
          .sort((a, b) => new Date(a.datetime!).getTime() - new Date(b.datetime!).getTime());
      }
    }
    return undefined;
  }, [sortedSeriesEvents, enrichedEvents, event.id, event.seriesId]);

  const [seriesEndDate, setSeriesEndDate] = useState<Date | undefined>();
  const [imageLoaded, setImageLoaded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Dégradé de fond basé sur les tags (events sans image)
  const fallbackGradient = useMemo(() => getTagFallbackGradient(event.tags), [event.tags]);

  // Visibility tracking: 80% threshold, accumulated duration, sent on unmount
  // scrollContainerRef scopes observation to the drawer's scroll area (not full viewport)
  useVisibilityTracker(cardRef, event, 'carte_preview', scrollContainerRef);

  // 🚀 OPTIMISATION : Mémoriser les calculs de date
  const dateTimeData = useMemo(() => formatEventDateTime(event.datetime, event.timezone), [event.datetime, event.timezone]);
  const { date, time } = dateTimeData;

  // 🚀 OPTIMISATION : Handler stable
  const handleSelect = useCallback((evt: Event) => {
    analytics.trackEventClick(evt, 'carte_preview');
    onSelect(evt);
  }, [analytics, onSelect]);

  return (
    <div ref={cardRef}>
    <ContentBlock
      variant="light"
      padding="sm"
      border="light"
      className="preview-card-map__card"
      onClick={() => handleSelect(event)}
    >
      <div className="preview-card-map__tags">
        {/* Prix et tags */}
        <div className="event-tags">
          {sortTags(event.tags).slice(0, 3).map((tag: string) => (
            <Tag
              key={tag}
              label={tag}
              type="activity"
              size="sm"
            />
          ))}
          {event.prices && event.prices.length > 0 && (
            event.prices.every(p => p.amount === 0) ? (
              <Tag
                label="Free"
                type="price"
                isFree={true}
                size="sm"
              />
            ) : event.prices.length === 1 && event.prices[0].amount !== null ? (
              <Tag
                label={`${event.prices[0].amount} ${event.prices[0].currency}`}
                type="price"
                size="sm"
              />
            ) : event.prices.length === 1 && event.prices[0].amount === null ? (
              <Tag
                label={event.prices[0].description || 'Contact organizer'}
                type="price"
                size="sm"
              />
            ) : null
          )}
        </div>
      </div>

      <div className="preview-card-map__main-content">
        <div
          className="preview-card-map__image"
          style={!event.image && fallbackGradient ? { background: fallbackGradient } : undefined}
        >
          {event.image ? (
            <img
              src={optimizeCloudinaryUrl(event.image, { width: 400 })}
              alt={event.title}
              className={`event-image ${imageLoaded ? 'loaded' : ''}`}
              onLoad={() => setImageLoaded(true)}
              loading="lazy"
            />
          ) : (
            <img
              src={FALLBACK_IMAGE}
              alt=""
              className="event-fallback-image"
              loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
            />
          )}
        </div>

        <div className="preview-card-map__content">
          <div className="preview-card-map__header">
            <h3 className="preview-card-map__title">{event.title}</h3>
            <EventDetails
              event={event}
              onEndDateChange={setSeriesEndDate}
              seriesEvents={eventSeriesEvents}
            />
          </div>

          <div className="preview-card-map__footer">
            <Button variant="outline" size="sm" className="preview-card-map__check-details">
              Check details
            </Button>
            <div className="preview-card-map__actions" onClick={(e) => e.stopPropagation()}>
              <ShareButton
                shareData={useMemo(() => ({
                  title: event.title,
                  text: `what2do.today\nCome discover ${event.title},\non ${date} at ${time}\n`,
                  url: window.location.href
                }), [event.title, date, time])}
                event={event}
                analyticsContext="carte_preview"
                variant="icon"
                size="lg"
                className="share-button"
                shouldTriggerSuccess={false}
                onShareSuccess={onShareSuccess}
              />
              <FavoriteButton
                id={event.id}
                seriesId={event.seriesId}
                expiresAt={seriesEndDate || event.datetime}
                seriesEvents={sortedSeriesEvents}
                fullEvent={event}
                size="lg"
                className="favorite-button"
                analyticsContext="carte_preview"
              />
            </div>
          </div>
        </div>
      </div>
    </ContentBlock>
    </div>
  );
});

// 🚀 OPTIMISATION PERFORMANCE : Comparaison custom pour éviter re-renders inutiles
const PreviewCardMapComponent = function PreviewCardMap({
  events,
  location,
  isOpen,
  onClose,
  onEventClick,
  isSingleEvent,
  seriesEvents,
  enrichedEvents // 🆕 Recevoir les données enrichies
}: PreviewCardMapProps) {
  // 🚀 OPTIMISATION CRITIQUE : Supprimer locationState instable
  // On utilise directement les props ou un state plus stable
  const [showNotif, setShowNotif] = useState(false);
  const analytics = useAnalytics();
  const listRef = useRef<HTMLDivElement>(null);

  const sortedSeriesEvents = useMemo(() => {
    if (!seriesEvents) return undefined;
    
    const now = new Date();
    const filtered = seriesEvents.filter(event => {
      if (!event.datetime) return false;
      const eventDate = new Date(event.datetime);
      return eventDate > now;
    });

    const sorted = filtered.sort((a, b) => {
      if (!a.datetime || !b.datetime) return 0;
      const dateA = new Date(a.datetime);
      const dateB = new Date(b.datetime);
      return dateA.getTime() - dateB.getTime();
    });

    return sorted;
  }, [seriesEvents]);

  // 🚀 SIMPLIFICATION DRASTIQUE : Seulement sous-titre avec comptage d'événements
  const title = events.length > 1 ? `${events.length} events` : "Event";
  const subtitle = undefined;

  // 🔄 PHASE 4 PRD Navigation : Handlers simplifiés
  // La navigation est maintenant gérée par HomePage via onEventClick (useModalStack)
  const handlers = useStableHandlers({
    handleClose: () => {
      console.log('🔙 [PreviewCardMap] Fermeture via onClose');
      onClose();
    },
    handleEventSelect: (event: Event) => {
      console.log('🎭 [PreviewCardMap] Clic event, délégation à onEventClick:', event.id);
      // 🔄 PHASE 4 : Déléguer à HomePage qui gère la navigation via useModalStack
      onEventClick(event);
    }
  }, [onClose, onEventClick]); // 🔄 PHASE 4 : Dépendances simplifiées

  const { handleClose, handleEventSelect } = handlers;

  // 🔧 FIX PHASE 4 v5: Utiliser mousedown au lieu de click
  // mousedown est déclenché AVANT que MapLibre n'intercepte l'événement
  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalMouseDown = (e: MouseEvent) => {
      // Ignorer si le clic est dans le drawer (via sélecteur CSS)
      const drawerContainer = document.querySelector('.map-drawer-container');
      if (drawerContainer?.contains(e.target as Node)) {
        return;
      }

      // 🔧 FIX: Ignorer les clics sur les markers de la carte
      // Le flux openPreviewCardMap gèrera le REPLACE automatiquement
      const target = e.target as HTMLElement;
      if (target.closest('.map-marker') || target.closest('.custom-marker-icon') || target.closest('.maplibregl-marker')) {
        console.log('📍 [PreviewCardMap] MouseDown sur un marker - laisser openPreviewCardMap gérer le REPLACE');
        return;
      }

      console.log('🎯 [PreviewCardMap] MouseDown en dehors du drawer - fermeture');
      handleClose();
    };

    // Délai pour éviter de fermer immédiatement si on vient de cliquer pour ouvrir
    const timer = setTimeout(() => {
      console.log('👂 [PreviewCardMap] Listener mousedown attaché');
      document.addEventListener('mousedown', handleGlobalMouseDown);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleGlobalMouseDown);
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <MapDrawer
        isOpen={isOpen}
        onClose={handleClose}
        className={isSingleEvent ? 'map-drawer--single' : ''}
        title={title}
        subtitle={subtitle}
        hasOverlay={false}
      >
      <div ref={listRef} className="preview-card-map__list" role="list">
        {events.map(event => (
          <EventCard
            key={event.id}
            event={event}
            onSelect={handleEventSelect}
            onShareSuccess={() => setShowNotif(true)}
            sortedSeriesEvents={sortedSeriesEvents}
            enrichedEvents={enrichedEvents}
            analytics={analytics}
            scrollContainerRef={listRef}
          />
        ))}
      </div>
      <NotificationBubble
        show={showNotif}
        message="Link copied! ✨"
        type="success"
        duration={3000}
        onHide={() => setShowNotif(false)}
      />
    </MapDrawer>
  );
};

// 🚀 OPTIMISATION CRITIQUE : React.memo avec comparaison custom pour enrichedEvents
// ✅ FIX: Inclure updatedAt pour détecter les modifications d'events en real-time
const getEventSignature = (e: any): string => {
  const updatedAt = e.updatedAt instanceof Date ? e.updatedAt.getTime() : (e.updatedAt || '');
  return `${e.id}:${updatedAt}`;
};

export default React.memo(PreviewCardMapComponent, (prevProps, nextProps) => {
  // Comparaison rapide des props simples
  if (
    prevProps.isOpen !== nextProps.isOpen ||
    prevProps.location !== nextProps.location ||
    prevProps.isSingleEvent !== nextProps.isSingleEvent ||
    prevProps.events.length !== nextProps.events.length
  ) {
    return false; // Props différentes = re-render nécessaire
  }

  // ✅ FIX: Comparaison des events par ID + updatedAt (pour détecter les modifications)
  const prevEventSigs = prevProps.events.map(getEventSignature).sort().join(',');
  const nextEventSigs = nextProps.events.map(getEventSignature).sort().join(',');
  if (prevEventSigs !== nextEventSigs) {
    return false; // Events modifiés = re-render nécessaire
  }

  // 🚀 OPTIMISATION CLÉE : Comparaison des enrichedEvents par signature stable
  if (prevProps.enrichedEvents?.length !== nextProps.enrichedEvents?.length) {
    return false;
  }

  if (prevProps.enrichedEvents && nextProps.enrichedEvents) {
    // ✅ FIX: Inclure updatedAt dans la signature pour détecter les modifications
    // 🔄 PHASE 4 : Accès sécurisé à updatedAt avec le type union EnrichedEvent
    const getUpdatedAtValue = (main: any): string => {
      const updatedAt = main?.updatedAt;
      return updatedAt instanceof Date ? updatedAt.getTime().toString() : (updatedAt || '');
    };

    const prevSignatures = prevProps.enrichedEvents.map(e => {
      const mainUpdatedAt = getUpdatedAtValue(e.main);
      return e._signature || `${e.main.id}:${e.series.length}:${mainUpdatedAt}`;
    }).join(',');
    const nextSignatures = nextProps.enrichedEvents.map(e => {
      const mainUpdatedAt = getUpdatedAtValue(e.main);
      return e._signature || `${e.main.id}:${e.series.length}:${mainUpdatedAt}`;
    }).join(',');

    if (prevSignatures !== nextSignatures) {
      return false; // Signatures différentes = re-render nécessaire
    }
  }

  // Comparaison des fonctions (handlers stables)
  if (
    prevProps.onClose !== nextProps.onClose ||
    prevProps.onEventClick !== nextProps.onEventClick
  ) {
    return false;
  }

  // Toutes les props importantes sont identiques = pas de re-render
  return true;
});